import { getAuthHeaders } from "../../../utils/authHeaders";
import {
    isSquaresDemoMatchId,
    squaresDemoCalculateOverOdds,
    squaresDemoMatchOverPlays,
    squaresDemoMatchSocial,
    squaresDemoMyOversInMatch,
    squaresDemoOverPlays,
    squaresDemoSubmit,
    squaresDemoTrySettleOver,
} from "../lib/squaresDemoApi";
import {
    fetchLeagueSquaresMyOversIfActive,
    fetchLeagueSquaresRoomIfActive,
    resolveActiveLeagueIdForSquaresRoom,
} from "../../Leagues/lib/leagueSquaresRoomApi";

const EMPTY_MATCH_OVER_PLAYS = { plays: [], overs: {}, overOddsLocked: {} };

export function squaresApiBaseUrl() {
  return (process.env.API_URL || "").replace(/\/$/, "");
}

function jsonAuthHeaders() {
  return { "Content-Type": "application/json", ...getAuthHeaders() };
}

/**
 * @param {URLSearchParams | Record<string, string>} searchParams
 * @returns {Promise<{ plays?: unknown[]; overs?: Record<string, unknown>; overOddsLocked?: Record<string, unknown> }>}
 */
/**
 * Friends playing this over + unread challenge notifications for this match (Squares match play).
 * Query: match_id (required), innings_number, over_number (omit when match finished / over unknown).
 * @param {URLSearchParams | Record<string, string>} searchParams
 * @returns {Promise<{ friends_on_over?: { user_id: number; name: string }[]; challenges?: { notification_id: number; sender_user_id: number; name: string }[] }>}
 */
export function fetchSquaresMatchSocial(searchParams) {
  const q = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
  const matchId = q.get("match_id") ?? q.get("matchId") ?? "";
  // League room is members-only — don't surface public Squares friends/challenges.
  if (resolveActiveLeagueIdForSquaresRoom()) {
    return Promise.resolve({ friends_on_over: [], challenges: [] });
  }
  if (isSquaresDemoMatchId(matchId)) return squaresDemoMatchSocial();
  const url = `${squaresApiBaseUrl()}/account/friends/squares-match-social?${q.toString()}`;
  return fetch(url, { method: "GET", headers: getAuthHeaders() }).then((res) =>
    res.ok ? res.json() : { friends_on_over: [], challenges: [] }
  );
}

export function fetchSquaresMatchOverPlays(searchParams) {
  const q = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
  // League mode first: prototype rooms restore league.plays (Over 1), not the demo over-12 script.
  return fetchLeagueSquaresRoomIfActive(q).then((leagueRoom) => {
    if (leagueRoom) return leagueRoom;
    const demo = squaresDemoMatchOverPlays(q);
    if (demo) return demo;
    const url = `${squaresApiBaseUrl()}/squares/match-over-plays?${q.toString()}`;
    return fetch(url, { method: "GET", headers: getAuthHeaders() }).then((res) =>
      res.ok ? res.json() : { ...EMPTY_MATCH_OVER_PLAYS }
    );
  });
}

/**
 * @param {URLSearchParams | Record<string, string>} searchParams
 * @returns {Promise<{ plays?: unknown[] }>}
 */
export function fetchSquaresOverPlays(searchParams) {
  const q = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
  return fetchLeagueSquaresRoomIfActive(q).then((leagueRoom) => {
    if (leagueRoom) return leagueRoom;
    const demo = squaresDemoOverPlays(q);
    if (demo) return demo;
    const url = `${squaresApiBaseUrl()}/squares/over-plays?${q.toString()}`;
    return fetch(url, { method: "GET", headers: getAuthHeaders() }).then((res) =>
      res.ok ? res.json() : { plays: [] }
    );
  });
}

/**
 * @param {string} sessionToken
 * @param {{ matchId: string; inningsNumber: number }} query
 */
export function fetchSquaresMyOversInMatch(sessionToken, { matchId, inningsNumber }, viewerUserId) {
  return fetchLeagueSquaresMyOversIfActive(matchId, inningsNumber).then((leagueMine) => {
    if (leagueMine) return leagueMine;
    const demo = squaresDemoMyOversInMatch(sessionToken, { matchId, inningsNumber }, viewerUserId);
    if (demo) return demo;
    const q = new URLSearchParams({
      matchId: String(matchId),
      inningsNumber: String(inningsNumber),
    });
    const url = `${squaresApiBaseUrl()}/squares/my-overs-in-match/${encodeURIComponent(sessionToken)}?${q.toString()}`;
    return fetch(url, { method: "GET", headers: getAuthHeaders() }).then((res) =>
      res.ok ? res.json() : { overNumbers: [] }
    );
  });
}

/** @param {string} sessionToken */
export function fetchSquaresMyMatches(sessionToken) {
  const url = `${squaresApiBaseUrl()}/squares/my-matches/${encodeURIComponent(sessionToken)}`;
  return fetch(url, { method: "GET", headers: getAuthHeaders() }).then((res) =>
    res.ok ? res.json() : { matches: [] }
  );
}

/** @param {{ matchId: string; inningsNumber: number; overNumber: number; session_token?: string }} body — include session_token when using session cookie auth without JWT */
export function postSquaresTrySettleOver(body) {
  if (isSquaresDemoMatchId(body?.matchId)) return squaresDemoTrySettleOver();
  // League Squares settles via leagueSquaresSettlement — never public squares_plays.
  if (resolveActiveLeagueIdForSquaresRoom()) {
    return Promise.resolve(new Response(null, { status: 204 }));
  }
  const url = `${squaresApiBaseUrl()}/squares/try-settle-over`;
  return fetch(url, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify(body),
  });
}

/** @param {string} sessionToken @param {Record<string, unknown>} body @param {object} [userAccount] — used by dev demo submit only */
export async function postSquaresSubmit(sessionToken, body, userAccount) {
  const demo = await squaresDemoSubmit(sessionToken, body, userAccount);
  if (demo) return demo;
  const url = `${squaresApiBaseUrl()}/squares/submit/${encodeURIComponent(sessionToken)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

/** @param {Record<string, unknown>} body — matchId, inningsNumber, overNumber, maxOvers, optional 410 fields */
export async function postSquaresCalculateOverOdds(body) {
  const demo = squaresDemoCalculateOverOdds(body);
  if (demo) return demo;
  const url = `${squaresApiBaseUrl()}/squares/calculate-over-odds`;
  const res = await fetch(url, {
    method: "POST",
    headers: jsonAuthHeaders(),
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) return null;
  return data;
}

/** @returns {Promise<{ events?: unknown[] } & Record<string, unknown>>} */
export function fetchSquaresPrioritisedMatches() {
  const url = `${squaresApiBaseUrl()}/squares/prioritised-matches`;
  return fetch(url, { method: "GET", headers: getAuthHeaders() }).then(async (res) => {
    if (!res.ok) return { events: [] };
    try {
      return await res.json();
    } catch {
      return { events: [] };
    }
  });
}

/**
 * @param {string} sessionToken
 * @param {{ page: number; perpage: number; states?: string; dateFrom?: string; dateTo?: string }} query
 * @returns {Promise<{ ok: boolean; data: unknown[]; count: number }>}
 */
export async function fetchSquaresMySquares(sessionToken, query) {
  const { page, perpage, states, dateFrom, dateTo } = query;
  const q = new URLSearchParams({ page: String(page), perpage: String(perpage) });
  if (states) q.set("states", states);
  if (dateFrom) q.set("dateFrom", dateFrom);
  if (dateTo) q.set("dateTo", dateTo);
  const url = `${squaresApiBaseUrl()}/squares/mysquares/${encodeURIComponent(sessionToken)}?${q.toString()}`;
  const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
  if (!res.ok) return { ok: false, data: [], count: 0 };
  const result = await res.json().catch(() => ({}));
  return {
    ok: true,
    data: Array.isArray(result.data) ? result.data : [],
    count: typeof result.count === "number" ? result.count : 0,
  };
}
